import { useState, useEffect } from 'react';
import { Download, TrendingUp, TrendingDown, Calendar, DollarSign } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface CFLineItem {
  id: string;
  name: string;
  type: 'header' | 'subheader' | 'line_item' | 'subtotal' | 'calculated';
  category?: string;
  level: number;
  value?: number;
  formula?: string;
  highlight?: boolean;
  bold?: boolean;
  children?: CFLineItem[];
}

interface CFStatement {
  id: string;
  organizationId: string;
  templateId?: string;
  method: string;
  periodStart: string;
  periodEnd: string;
  periodType: string;
  scenario: string;
  data: {
    lineItems: CFLineItem[];
  };
  totals: {
    netCashOperating: number;
    netCashInvesting: number;
    netCashFinancing: number;
    netChangeCash: number;
    cashBeginning: number;
    cashEnding: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface CashFlowStatementProps {
  statementId?: string;
  onGenerate?: () => void;
}

const CashFlowStatement = ({ statementId, onGenerate }: CashFlowStatementProps) => {
  const [statement, setStatement] = useState<CFStatement | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (statementId) {
      loadStatement();
    }
  }, [statementId]);

  const loadStatement = async () => {
    if (!statementId) return;

    setLoading(true);
    try {
      const response = await api.get(`/cash-flow/statements/${statementId}`);
      setStatement(response.data.data);
    } catch (error: any) {
      toast.error('Failed to load cash flow statement');
      console.error('Error loading statement:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '$0.00';

    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return value < 0 ? `($${formatted})` : `$${formatted}`;
  };

  const getCashFlowColor = (value: number): string => {
    if (value > 0) return 'text-success-600';
    if (value < 0) return 'text-alert-600';
    return 'text-neutral-900';
  };

  const renderLineItem = (item: CFLineItem, index: number) => {
    const isHeader = item.type === 'header';
    const isSubheader = item.type === 'subheader';
    const isSubtotal = item.type === 'subtotal' || item.type === 'calculated';
    const indentClass = `pl-${Math.min(item.level * 6, 24)}`;

    return (
      <div key={item.id || index}>
        <div
          className={`grid grid-cols-2 py-2 border-b border-neutral-100 hover:bg-neutral-50 ${
            item.highlight ? 'bg-primary-50' : ''
          } ${isSubtotal ? 'border-t-2 border-neutral-300 font-semibold' : ''} ${
            isHeader ? 'bg-neutral-100 font-bold' : ''
          }`}
        >
          <div
            className={`${indentClass} ${
              item.bold || isSubtotal || isHeader ? 'font-bold' : ''
            } ${
              isHeader
                ? 'font-bold text-neutral-900 uppercase text-sm'
                : isSubheader
                ? 'font-semibold text-neutral-700 italic'
                : 'text-neutral-900'
            }`}
          >
            {item.name}
          </div>
          <div
            className={`text-right ${item.bold || isSubtotal ? 'font-bold' : ''} ${
              isHeader || isSubheader ? '' : getCashFlowColor(item.value || 0)
            }`}
          >
            {!isHeader && !isSubheader && formatCurrency(item.value)}
          </div>
        </div>

        {item.children && item.children.map((child, idx) => renderLineItem(child, idx))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="card text-center py-12">
        <DollarSign className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">
          No Cash Flow Statement
        </h3>
        <p className="text-neutral-600 mb-4">
          Generate a cash flow statement to get started
        </p>
        {onGenerate && (
          <button onClick={onGenerate} className="btn btn-primary">
            Generate Cash Flow Statement
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Cash Flow Statement
              <span className="ml-3 text-sm font-normal text-neutral-600 capitalize">
                ({statement.method} Method)
              </span>
            </h2>
            <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-600">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>
                  {new Date(statement.periodStart).toLocaleDateString()} -{' '}
                  {new Date(statement.periodEnd).toLocaleDateString()}
                </span>
              </div>
              <span>•</span>
              <span className="capitalize">{statement.periodType}</span>
              <span>•</span>
              <span className="capitalize bg-primary-100 text-primary-800 px-2 py-1 rounded">
                {statement.scenario}
              </span>
            </div>
          </div>

          <button className="btn btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div
            className={`p-4 rounded-lg border ${
              statement.totals.netCashOperating > 0
                ? 'bg-success-50 border-success-200'
                : 'bg-alert-50 border-alert-200'
            }`}
          >
            <p
              className={`text-sm mb-1 ${
                statement.totals.netCashOperating > 0
                  ? 'text-success-700'
                  : 'text-alert-700'
              }`}
            >
              Operating Activities
            </p>
            <p
              className={`text-2xl font-bold ${
                statement.totals.netCashOperating > 0
                  ? 'text-success-900'
                  : 'text-alert-900'
              }`}
            >
              {formatCurrency(statement.totals.netCashOperating)}
            </p>
            <div className="flex items-center mt-2">
              {statement.totals.netCashOperating > 0 ? (
                <TrendingUp className="w-4 h-4 text-success-600 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-alert-600 mr-1" />
              )}
              <span className="text-xs text-neutral-600">Cash from operations</span>
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              statement.totals.netCashInvesting < 0
                ? 'bg-neutral-50 border-neutral-200'
                : 'bg-success-50 border-success-200'
            }`}
          >
            <p className="text-sm text-neutral-700 mb-1">Investing Activities</p>
            <p className="text-2xl font-bold text-neutral-900">
              {formatCurrency(statement.totals.netCashInvesting)}
            </p>
            <p className="text-xs text-neutral-600 mt-2">
              {statement.totals.netCashInvesting < 0 ? 'Investing in growth' : 'Divesting assets'}
            </p>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              statement.totals.netCashFinancing > 0
                ? 'bg-primary-50 border-primary-200'
                : 'bg-neutral-50 border-neutral-200'
            }`}
          >
            <p
              className={`text-sm mb-1 ${
                statement.totals.netCashFinancing > 0
                  ? 'text-primary-700'
                  : 'text-neutral-700'
              }`}
            >
              Financing Activities
            </p>
            <p
              className={`text-2xl font-bold ${
                statement.totals.netCashFinancing > 0
                  ? 'text-primary-900'
                  : 'text-neutral-900'
              }`}
            >
              {formatCurrency(statement.totals.netCashFinancing)}
            </p>
            <p className="text-xs text-neutral-600 mt-2">
              {statement.totals.netCashFinancing > 0 ? 'Raising capital' : 'Returning capital'}
            </p>
          </div>
        </div>

        {/* Net Change Summary */}
        <div className="p-4 bg-neutral-900 rounded-lg text-white">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-neutral-400 mb-1">Net Change in Cash</p>
              <p className="text-2xl font-bold">
                {formatCurrency(statement.totals.netChangeCash)}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-400 mb-1">Beginning Cash</p>
              <p className="text-xl font-semibold">
                {formatCurrency(statement.totals.cashBeginning)}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-400 mb-1">Ending Cash</p>
              <p className="text-xl font-semibold">
                {formatCurrency(statement.totals.cashEnding)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Statement */}
      <div className="card">
        <div className="grid grid-cols-2 py-3 border-b-2 border-neutral-300 font-semibold text-sm text-neutral-700 bg-neutral-50">
          <div>Line Item</div>
          <div className="text-right">Amount</div>
        </div>

        <div className="divide-y divide-neutral-100">
          {statement.data.lineItems.map((item, index) => renderLineItem(item, index))}
        </div>
      </div>
    </div>
  );
};

export default CashFlowStatement;
